import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import {
  CreateApiTokenApiArg,
  CreateApiTokenApiResponse,
  DeleteApiTokenApiArg,
  DeleteApiTokenApiResponse,
  ReadApiTokensApiResponse,
  ReadOAuthProviderConnectionByIdArg,
  ReadOAuthProviderConnectionByIdResponse,
  ReadOAuthProviderConnectionStatusByIdArg,
  ReadOAuthProviderConnectionStatusByIdResponse,
} from './types/authApi.types';

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readOAuthProviderConnectionById: builder.query<
      ReadOAuthProviderConnectionByIdResponse,
      ReadOAuthProviderConnectionByIdArg
    >({
      query: ({ id }) => ({
        type: 'partnerIntegrations',
        path: `auth/provider/connect/${id}`,
      }),
    }),
    readOAuthProviderConnectionStatusById: builder.query<
      ReadOAuthProviderConnectionStatusByIdResponse,
      ReadOAuthProviderConnectionStatusByIdArg
    >({
      query: ({ id }) => ({
        type: 'partnerIntegrations',
        path: `auth/provider/status/${id}`,
      }),
    }),
    readApiTokens: builder.query<ReadApiTokensApiResponse, void>({
      query: () => ({
        version: 1,
        path: 'auth/api-tokens',
      }),
    }),
    createApiToken: builder.mutation<
      CreateApiTokenApiResponse,
      CreateApiTokenApiArg
    >({
      query: ({ name, scopes, version }) => ({
        version: 1,
        method: 'POST',
        path: version === '2' ? 'auth/api-tokens-v2' : 'auth/api-tokens',
        body: { name, scopes, version },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;

          dispatch(
            authApi.util.updateQueryData(
              'readApiTokens',
              undefined,
              (draft) => {
                const tokens = draft.data.tokens as DeepWritable<
                  typeof draft.data.tokens
                >;

                tokens.push(
                  response.data.data as DeepWritable<typeof response.data.data>,
                );
              },
            ),
          );
        } catch {
          // Not doing anything here.
        }
      },
    }),
    deleteApiToken: builder.mutation<
      DeleteApiTokenApiResponse,
      DeleteApiTokenApiArg
    >({
      query: ({ tokenId }) => ({
        version: 1,
        method: 'DELETE',
        path: `auth/api-tokens/${tokenId}`,
      }),
      async onQueryStarted({ tokenId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          authApi.util.updateQueryData('readApiTokens', undefined, (draft) => {
            const tokens = draft.data.tokens as DeepWritable<
              typeof draft.data.tokens
            >;
            tokens.splice(
              tokens.findIndex(({ id }) => id === tokenId),
              1,
            );
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});
